<script setup lang="ts">
import { bindField, revealErrors } from '#core/utils/bind-field'
import { Field, FieldContent, FieldError, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { Button } from '@forgd/ui-kit/button'
import { Input } from '@forgd/ui-kit/input'
import { ArrowLeftIcon } from '@heroicons/vue/24/outline'
import { revalidateLogic, useForm } from '@tanstack/vue-form'
import { z } from 'zod'

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'login'): void
  (e: 'register'): void
}>()

const schema = z.object({
  email: z.string().trim().email('Email is required'),
})

const form = useForm({
  defaultValues: {
    email: '',
  },
  validationLogic: revalidateLogic({ mode: 'change', modeAfterSubmission: 'change' }),
  onSubmitInvalid: ({ formApi }) => revealErrors(formApi),
  validators: { onDynamic: schema },
  onSubmit: ({ value }) => submit(value.email),
})

const loading = ref(false)
const sent = ref(false)
const notFound = ref(false)

async function submit(email: string) {
  if (loading.value) {
    return
  }
  loading.value = true
  notFound.value = false

  try {
    await apiFetch('/auth/reset-password', {
      method: 'post',
      params: { body: { email: email.trim() } },
    })
    sent.value = true
  }
  catch {
    notFound.value = true
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="flex w-full flex-col gap-5">
    <Button variant="outline" size="sm" class="self-start" @click="emit('back')">
      <ArrowLeftIcon aria-hidden="true" />
      Back
    </Button>

    <div>
      <h1 class="text-lg font-semibold text-primal">
        Recover your password
      </h1>
      <p class="text-sm text-secondary">
        Enter the email address used to create your account.<br>
        We will send you an email to reset your password.
      </p>
    </div>

    <div class="flex flex-col gap-3">
      <div
        v-if="sent"
        class="flex items-center gap-3 rounded-lg bg-neutral-300 p-5"
        data-testid="forgotPasswordSuccessMessage"
      >
        <img src="/images/success.svg" alt="Success" class="size-[108px] shrink-0">
        <div class="flex flex-col gap-2">
          <p class="text-lg font-semibold text-primal">
            An email with a link to reset your password has been sent.
          </p>
          <p class="text-sm text-secondary">
            You can close this window now.
          </p>
        </div>
      </div>

      <form v-else class="space-y-3" novalidate @submit.prevent="form.handleSubmit()">
        <form.Field v-slot="{ field }" name="email">
          <Field>
            <FieldLabel v-bind="bindField(field).label">
              Email
            </FieldLabel>
            <FieldContent>
              <Input
                v-bind="bindField(field).control"
                :name="field.name"
                :error="bindField(field).control.error || notFound"
                type="text"
                size="md"
                placeholder="e.g., youruser@email.com"
                @update:model-value="notFound = false"
              />
              <FieldError v-bind="bindField(field).error" />
              <p v-if="notFound" class="text-sm text-error" data-testid="forgotPasswordErrorMessage">
                This email does not have an account on Forgd
              </p>
            </FieldContent>
          </Field>
        </form.Field>

        <Button
          size="lg"
          class="h-12"
          block
          type="submit"
          :loading="loading"
        >
          Send Recovery Email
        </Button>
      </form>

      <div class="flex flex-col gap-2 text-sm">
        <button type="button" class="self-start font-semibold text-primary-300" @click="emit('login')">
          Sign in
        </button>
        <p class="text-gray-600">
          Don’t have an account yet?
          <button type="button" class="font-semibold text-primary-300" @click="emit('register')">
            Join Forgd for Free
          </button>
        </p>
      </div>
    </div>
  </div>
</template>
